import { DateTime, Duration } from "luxon";
import { z } from "zod";
import {
  stringifyStructuredContentAsMarkdown,
  stringifyStructuredContentPhrasingAsMarkdown,
  stringifyStructuredContentPhrasingRunAsMarkdown
} from "../content/structured/helpers.js";
import { assertUnreachable, isNotUndefined } from "../helpers/utilities.js";
import {
  collectPlaybookCriteria
} from "./playbook.js";
const COACHING_PERIOD_DURATION_DAYS = 7;
const COACHING_PERIOD_DURATION = Duration.fromObject({
  days: COACHING_PERIOD_DURATION_DAYS
});
function parseUtcInstant(iso) {
  const parsed = DateTime.fromISO(iso, { zone: "utc" });
  if (!parsed.isValid) {
    throw new Error(`Invalid coaching boundary instant: ${iso}`);
  }
  return parsed;
}
const LAST_FRIDAY_COACHING_BOUNDARY = parseUtcInstant(
  "2026-08-07T02:00:00.000Z"
);
const FIRST_MONDAY_COACHING_BOUNDARY = parseUtcInstant(
  "2026-08-17T02:00:00.000Z"
);
const COACHING_BOUNDARY_HOUR_UTC = 2;
function calculateMostRecentCoachingPeriodBoundary(referenceDate) {
  const utcReferenceDate = referenceDate.toUTC();
  if (utcReferenceDate >= FIRST_MONDAY_COACHING_BOUNDARY) {
    const weeks = Math.floor(
      utcReferenceDate.diff(FIRST_MONDAY_COACHING_BOUNDARY, "weeks").weeks
    );
    return FIRST_MONDAY_COACHING_BOUNDARY.plus({ weeks });
  }
  let boundary = utcReferenceDate.startOf("day").set({ hour: COACHING_BOUNDARY_HOUR_UTC });
  if (boundary > utcReferenceDate) {
    boundary = boundary.minus({ days: 1 });
  }
  while (boundary.weekday !== 5) {
    boundary = boundary.minus({ days: 1 });
  }
  return boundary < LAST_FRIDAY_COACHING_BOUNDARY ? boundary : LAST_FRIDAY_COACHING_BOUNDARY;
}
function calculateNextCoachingPeriodBoundary(boundary) {
  return boundary.toMillis() === LAST_FRIDAY_COACHING_BOUNDARY.toMillis() ? FIRST_MONDAY_COACHING_BOUNDARY : boundary.plus({ weeks: 1 });
}
function calculatePreviousCoachingPeriodBoundary(boundary) {
  return boundary.toMillis() === FIRST_MONDAY_COACHING_BOUNDARY.toMillis() ? LAST_FRIDAY_COACHING_BOUNDARY : boundary.minus({ weeks: 1 });
}
function checkIsCanonicalCoachingPeriodBoundary(instant) {
  const utcInstant = instant.toUTC();
  if (utcInstant.hour !== COACHING_BOUNDARY_HOUR_UTC || utcInstant.minute !== 0 || utcInstant.second !== 0 || utcInstant.millisecond !== 0) {
    return false;
  }
  if (utcInstant > LAST_FRIDAY_COACHING_BOUNDARY && utcInstant < FIRST_MONDAY_COACHING_BOUNDARY) {
    return false;
  }
  return utcInstant <= LAST_FRIDAY_COACHING_BOUNDARY ? utcInstant.weekday === 5 : utcInstant.weekday === 1;
}
const teammatePlaybookCoachingIdSchema = z.uuid().brand();
function asTeammatePlaybookCoachingId(id) {
  return teammatePlaybookCoachingIdSchema.parse(id);
}
const PLAYBOOK_COACHING_SINGLE_NOTE_PROMPT_VERSION = "single_note_v3";
function matchZoneOf(reference, instant) {
  const rezoned = instant.setZone(reference.zone);
  if (!rezoned.isValid) {
    throw new Error("Expected coaching boundary to stay valid across zones");
  }
  return rezoned;
}
function computeCoachingDatesFromPeriod(periodStart, periodEnd) {
  const periodDuration = periodEnd.diff(periodStart);
  const isCanonicalPeriodEnd = checkIsCanonicalCoachingPeriodBoundary(periodEnd);
  const previousPeriodEnd = isCanonicalPeriodEnd ? matchZoneOf(
    periodEnd,
    calculatePreviousCoachingPeriodBoundary(periodEnd.toUTC())
  ) : periodStart;
  const goalEvaluationWindowEnd = isCanonicalPeriodEnd ? matchZoneOf(
    periodEnd,
    calculateNextCoachingPeriodBoundary(periodEnd.toUTC())
  ) : periodEnd.plus(periodDuration);
  return {
    periodStart,
    periodEnd,
    previousPeriodStart: previousPeriodEnd.minus(periodDuration),
    previousPeriodEnd,
    goalEvaluationWindowStart: periodEnd,
    goalEvaluationWindowEnd
  };
}
function computeTeammatePlaybookCoachingDates(coaching) {
  const periodStart = DateTime.fromISO(coaching.content.min_last_recorded_at);
  if (!periodStart.isValid) {
    throw new Error(
      "Expected coaching content min_last_recorded_at to be a valid ISO date"
    );
  }
  const periodEnd = DateTime.fromISO(coaching.content.max_last_recorded_at);
  if (!periodEnd.isValid) {
    throw new Error(
      "Expected coaching content max_last_recorded_at to be a valid ISO date"
    );
  }
  return computeCoachingDatesFromPeriod(periodStart, periodEnd);
}
function computeUpcomingTeammatePlaybookCoachingDates(periodEnd) {
  return computeCoachingDatesFromPeriod(
    periodEnd.minus({ days: COACHING_PERIOD_DURATION_DAYS }),
    periodEnd
  );
}
function formatGoalEvaluationWindowLabel(coachingDates) {
  const from = coachingDates.goalEvaluationWindowStart;
  const to = coachingDates.goalEvaluationWindowEnd;
  return `${from.toFormat("EEE MMM dd")} \u2013 ${to.toFormat("EEE MMM dd")}`;
}
function getCoachingGoalCallCount(goal) {
  return goal && goal.type !== "score_target" ? goal.goal_call_count : void 0;
}
function prepareCoachingContentCriteria(tree, content) {
  const allCriteria = collectPlaybookCriteria(tree);
  return content.criteria.map((criterionCoaching) => {
    const criterion = allCriteria.find(
      (candidate) => candidate.id === criterionCoaching.criterion_id
    );
    if (criterion === void 0) {
      return void 0;
    }
    return {
      criterion,
      coaching: criterionCoaching
    };
  }).filter(isNotUndefined);
}
function selectScoreTargetCoachingFocus(tree, content) {
  const scoreTargetCriteria = prepareCoachingContentCriteria(
    tree,
    content
  ).filter(({ coaching }) => coaching.goal?.type === "score_target");
  if (scoreTargetCriteria.length !== 1) {
    return void 0;
  }
  const focus = scoreTargetCriteria[0];
  if (focus === void 0) {
    return void 0;
  }
  const { goal } = focus.coaching;
  const startedScore = focus.coaching.teammate_average_score;
  if (goal?.type !== "score_target" || startedScore === null) {
    return void 0;
  }
  return {
    criterion: focus.criterion,
    goal,
    startedScore
  };
}
function stringifyCriterionCoachingContent(content) {
  return stringifyStructuredContentAsMarkdown(
    convertCoachingContentToStructuredContent(content)
  );
}
function convertCoachingContentToStructuredContent(content) {
  switch (content.v) {
    case void 0:
      return {
        type: "root",
        children: content.paragraphs.map((paragraph) => ({
          type: "paragraph",
          children: paragraph.fragments.map(
            convertLegacyCoachingFragmentToPhrasingContent
          )
        }))
      };
    case 2:
      return {
        type: "root",
        children: [
          ...convertCoachingSectionToStructuredContent(
            "What you're missing",
            content.notDoing
          ),
          ...convertCoachingSectionToStructuredContent(
            "How to improve",
            content.shouldBeDoing
          ),
          ...content.teamDoing ? convertCoachingSectionToStructuredContent(
            "Top examples from the team",
            content.teamDoing
          ) : []
        ]
      };
    case 3:
      return content;
    default:
      return assertUnreachable(content, () => ({
        type: "root",
        children: []
      }));
  }
}
function convertCoachingSectionToStructuredContent(heading, root) {
  return [
    { type: "heading", children: [{ type: "text", value: heading }] },
    ...root.children
  ];
}
function convertLegacyCoachingFragmentToPhrasingContent(fragment) {
  switch (fragment.type) {
    case "text":
      return { type: "text", value: fragment.text };
    case "quote":
      return {
        type: "quote",
        accountId: fragment.accountId,
        children: [{ type: "text", value: fragment.text }]
      };
    case "account_reference":
      return {
        type: "grading_reference",
        accountId: fragment.accountId,
        children: [{ type: "text", value: fragment.text }]
      };
  }
}
function convertCoachingAstToLegacyParagraphRun(root) {
  return root.children.flatMap((child) => {
    switch (child.type) {
      case "list":
        return child.children.map((item) => ({
          fragments: item.children.map(
            convertCoachingPhrasingContentToLegacyFragment
          )
        }));
      case "heading":
      case "paragraph":
        return [
          {
            fragments: child.children.map(
              convertCoachingPhrasingContentToLegacyFragment
            )
          }
        ];
      case "table":
        return child.children.map((row) => ({
          fragments: [
            {
              type: "text",
              text: row.children.map(
                (cell) => stringifyStructuredContentPhrasingRunAsMarkdown(
                  cell.children
                )
              ).join(" \xB7 ")
            }
          ]
        }));
      default:
        return assertUnreachable(child, () => []);
    }
  });
}
function convertCoachingPhrasingContentToLegacyFragment(content) {
  switch (content.type) {
    case "text":
      return {
        type: "text",
        text: content.value
      };
    case "quote":
      if (!content.accountId) {
        return {
          type: "text",
          text: `"${stringifyStructuredContentPhrasingRunAsMarkdown(
            content.children
          )}"`
        };
      }
      return {
        type: "quote",
        accountId: content.accountId,
        // We don't support nested formatting in the legacy format, so just stringify the text content.
        text: stringifyStructuredContentPhrasingRunAsMarkdown(content.children)
      };
    case "grading_reference":
      if (!content.accountId) {
        return {
          type: "text",
          text: stringifyStructuredContentPhrasingRunAsMarkdown(
            content.children
          )
        };
      }
      return {
        type: "account_reference",
        accountId: content.accountId,
        // We don't support nested formatting in the legacy format, so just stringify the text content.
        text: stringifyStructuredContentPhrasingRunAsMarkdown(content.children)
      };
    // The legacy format has neither highlights nor semantic references, so
    // keep just the text.
    case "highlight":
    case "teammate_reference":
    case "person_reference":
    case "date_reference":
      return {
        type: "text",
        // We don't support nested formatting in the legacy format, so just stringify the text content.
        text: stringifyStructuredContentPhrasingRunAsMarkdown(content.children)
      };
    case "emphasis":
    case "strong":
      return {
        type: "text",
        // We don't support nested formatting in the legacy format, so keep the
        // markdown markers in the flattened text.
        text: stringifyStructuredContentPhrasingAsMarkdown(content)
      };
    default:
      return assertUnreachable(content, () => ({
        type: "text",
        text: ""
      }));
  }
}
export {
  COACHING_PERIOD_DURATION,
  COACHING_PERIOD_DURATION_DAYS,
  FIRST_MONDAY_COACHING_BOUNDARY,
  LAST_FRIDAY_COACHING_BOUNDARY,
  PLAYBOOK_COACHING_SINGLE_NOTE_PROMPT_VERSION,
  asTeammatePlaybookCoachingId,
  calculateMostRecentCoachingPeriodBoundary,
  calculateNextCoachingPeriodBoundary,
  calculatePreviousCoachingPeriodBoundary,
  checkIsCanonicalCoachingPeriodBoundary,
  computeTeammatePlaybookCoachingDates,
  computeUpcomingTeammatePlaybookCoachingDates,
  convertCoachingAstToLegacyParagraphRun,
  convertCoachingContentToStructuredContent,
  formatGoalEvaluationWindowLabel,
  getCoachingGoalCallCount,
  prepareCoachingContentCriteria,
  selectScoreTargetCoachingFocus,
  stringifyCriterionCoachingContent,
  teammatePlaybookCoachingIdSchema
};
//# sourceMappingURL=teammatePlaybookCoaching.js.map
